import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { InvoiceHeaderComponent } from './invoice-header/invoice-header.component';
import { InvoiceListComponent } from './invoice-list/invoice-list.component';
import { FilteredSiteEntry, InvoiceEvent, SiteInvoice } from './invoice-item/invoice-item.component';
import { MonthRange } from './invoice-filter-bar/invoice-filter-bar.component';
import { TranslatorService } from '../../../services/translator_service';
import { ApiService } from '../../../web-services/api/api.service';
import { ApiQuery } from '../../../web-services/api/api.query';
import moment from 'moment';
import { UtilService } from '../../../services/util.service';

@Component({
  selector: 'app-invoices',
  standalone: true,
  imports: [CommonModule, TranslateModule, InvoiceHeaderComponent, InvoiceListComponent],
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss'
})
export class InvoicesComponent implements OnInit {
  period = '';
  grandTotal = '';
  searchQuery = '';
  sites: SiteInvoice[] = [];
  isLoading = true;
  allEventTypes: string[]   = [];
  selectedEventTypes: string[] = [];
  allSiteNames: string[]    = [];
  selectedSiteNames: string[]  = [];

  constructor(
    private translator: TranslatorService,
    private apiService: ApiService,
    private apiQuery: ApiQuery,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.translator.currentLangEmitter$.subscribe(value => {
      this.period = this.formatRange(this.currentRange, value as string);
    });

    combineLatest([
      this.apiQuery.contractsData$,
      this.apiQuery.contractStatusData$
    ]).pipe(
      filter(([contracts, status]) => !!contracts?.length && !!status?.contract_status?.length)
    ).subscribe(([contracts, status]: [any[], any]) => {
      this.buildSites(status, contracts);
    });
  }

  private buildSites(data: any, contracts: any[]): void {
    const isHebrew = this.translator.translate.currentLang === 'iw';
    const contractStatuses: any[] = data.contract_status || [];
    const newSites: SiteInvoice[] = [];
    let grandTotalAmount = 0;
    let grandCurrency = '';

    for (const contractStatus of contractStatuses) {
      const contractName = contractStatus.contract_name || `Contract #${contractStatus.contract_id}`;
      const matchedContract = contracts.find((c: any) => c.contract_id === contractStatus.contract_id);
      const firstEt = (matchedContract?.event_types || [])[0];
      const currency = firstEt
        ? (isHebrew ? firstEt.contract_currency_type_name_hebrew : firstEt.contract_currency_type_name) || ''
        : '';
      if (currency && !grandCurrency) grandCurrency = currency;

      for (const site of (contractStatus.sites || [])) {
        const siteName = site.site_name || `Site ${site.site_id}`;
        const events: InvoiceEvent[] = [];
        let siteTotal = 0;

        for (const bin of (site.bins || [])) {
          for (const event of (bin.events || [])) {
            const rate = +(event.rate || 0);
            siteTotal += rate;
            grandTotalAmount += rate;

            events.push({
              bin:       bin.bin_name    || '',
              binType:   isHebrew ? (bin.bin_type_name_hebrew || bin.bin_type_name || '') : (bin.bin_type_name || ''),
              date:      this.formatEventTime(event.event_time, isHebrew),
              eventType: isHebrew
                ? (event.event_type_name_hebrew || event.event_type_name || '')
                : (event.event_type_name || ''),
              units:     event.units     || 1,
              address:   bin.bin_address || bin.address           || '',
              contract:  contractName,
              reason:    event.reason?.type || '--',
              amount:    `${this.utilService.roundAmount(rate)} ${currency}`.trim()
            });
          }
        }

        newSites.push({
          siteName,
          monthlyFee:      '--',
          maintenanceFees: '--',
          credits:         0,
          units:           (site.bins || []).length,
          total:           `${this.utilService.roundAmount(siteTotal)} ${currency}`.trim(),
          expanded:        newSites.length === 0,
          events
        });
      }
    }

    this.sites = newSites;
    this.isLoading = false;
    this.grandTotal = `${this.utilService.roundAmount(grandTotalAmount)} ${grandCurrency}`.trim();

    const typeSet = new Set<string>();
    for (const site of newSites) {
      for (const ev of site.events) {
        if (ev.eventType) typeSet.add(ev.eventType);
      }
    }
    this.allEventTypes = Array.from(typeSet);
    this.allSiteNames  = newSites.filter(s => s.events.length > 0).map(s => s.siteName);
  }

  private currentRange: MonthRange = {
    start: { year: moment().year(), month: 0 },
    end:   { year: moment().year(), month: moment().month() }
  };

  dateRange: { start_date: string; end_date: string } = this.buildDateRange(this.currentRange);

  onEventTypeFilterChange(types: string[]): void { this.selectedEventTypes = types; }
  onSiteFilterChange(names: string[]): void { this.selectedSiteNames = names; }

  onMonthRangeChange(range: MonthRange): void {
    this.currentRange = range;
    this.period = this.formatRange(range, this.translator.translate.currentLang);
    this.dateRange = this.buildDateRange(range);
    this.callContractStatus();
  }

  private callContractStatus(): void {
    this.apiService.getContractStatus({
      contract_id: 0,
      start_date: this.dateRange.start_date,
      end_date: this.dateRange.end_date
    });
  }

  private formatEventTime(eventTime: any, isHebrew: boolean): string {
    if (!eventTime) { return ''; }
    return isHebrew
      ? this.utilService.convertDateHebrew(eventTime)
      : this.utilService.convertDateEnglish(eventTime);
  }

  private buildDateRange(range: MonthRange): { start_date: string; end_date: string } {
    const start = range.start
      ? moment({ year: range.start.year, month: range.start.month }).startOf('month').format('YYYY-MM-DD') + ' 00:00:01'
      : '';
    const endMonth = range.end ?? range.start;
    const end = endMonth
      ? moment({ year: endMonth.year, month: endMonth.month }).endOf('month').format('YYYY-MM-DD') + ' 23:59:59'
      : '';
    return { start_date: start, end_date: end };
  }

  private formatRange(range: MonthRange, langCode: string): string {
    if (!range.start) return '';
    const locale = langCode == 'iw' ? 'he' : langCode;
    const fmt = (m: { year: number; month: number }) => {
      const name = moment({ year: m.year, month: m.month }).locale(locale).format('MMMM');
      return `${name} ${m.year}`;
    };
    return range.end ? `${fmt(range.start)} – ${fmt(range.end)}` : fmt(range.start);
  }

  get filteredEntries(): FilteredSiteEntry[] {
    const q = this.searchQuery.trim().toLowerCase();
    const filterByType = this.selectedEventTypes.length > 0;
    const result: FilteredSiteEntry[] = [];

    const filterBySite = this.selectedSiteNames.length > 0;

    for (const s of this.sites) {
      if (filterBySite && !this.selectedSiteNames.includes(s.siteName)) continue;

      let events = s.events;

      if (filterByType) {
        events = events.filter(ev => this.selectedEventTypes.includes(ev.eventType));
      }

      if (q) {
        const hit = (val: string) => val.toLowerCase().includes(q);
        const siteHit = [s.siteName, String(s.units), s.total].some(hit);
        events = events.filter((ev: InvoiceEvent) =>
          siteHit || [ev.bin, ev.binType, ev.date, ev.eventType, String(ev.units),
                      ev.address, ev.contract, ev.reason, ev.amount].some(hit)
        );
      }

      if (events.length > 0) result.push({ site: s, events });
    }
    return result;
  }
}
